'use client';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, ClickAwayListener, Grid, IconButton, Menu, MenuItem, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CategoryScale, Chart as ChartJS, Tooltip as ChartTooltip, Filler, Legend, LinearScale, LineElement, PointElement } from 'chart.js';
import dayjs from 'dayjs';
import { BubbleChatIcon, Calendar04Icon, CellsIcon, Idea01Icon, InformationCircleIcon, MessageQuestionIcon } from 'hugeicons-react';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useGetMachineAnalyticsQuery } from '../../../../application/features/machines/machine-api';
import useTranslate from '../../../../localization/helpers';
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Filler, ChartTooltip, Legend);
var graphSetup = [
    {
        borderColor: 'rgba(255, 86, 48, 1)',
        backgroundColor: 'rgba(255, 86, 48, 0.05)',
        fill: true,
        tension: 0.4
    },
    {
        borderColor: 'rgba(0, 184, 217, 1)',
        backgroundColor: 'rgba(0, 184, 217, 0.05)',
        fill: true,
        tension: 0.4
    },
    {
        borderColor: 'rgba(255, 171, 0, 1)',
        backgroundColor: 'rgba(255, 171, 0, 0.05)',
        fill: true,
        tension: 0.4
    },
    {
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.05)',
        fill: true,
        tension: 0.4
    },
    {
        borderColor: 'rgba(0, 153, 131, 1)',
        backgroundColor: 'rgba(0, 153, 131, 0.05)',
        fill: true,
        tension: 0.4
    }
];
export default function Overview() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    var theme = useTheme();
    var isXs = useMediaQuery(theme.breakpoints.only('xs'));
    var isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
    var _k = __read(useState(false), 2), openTooltip = _k[0], setOpenTooltip = _k[1];
    var _l = __read(useState(false), 2), openAddedAssetsTooltip = _l[0], setOpenAddedAssetsTooltip = _l[1];
    var isscreen600900 = useMediaQuery('(min-width:600px) and (max-width:900px)');
    var translate = useTranslate().translate;
    var _m = __read(useState(null), 2), daysAnchorEl = _m[0], setDaysAnchorEl = _m[1];
    var _o = __read(useState({
        days: 7,
        dateRange: 'audit.days',
        startDate: dayjs().subtract(7, 'day').toDate(),
        endDate: dayjs().toDate()
    }), 2), analyticsFilters = _o[0], setAnalyticsFilters = _o[1];
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                        size: 12
                    },
                    generateLabels: function (chart) {
                        var labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                        labels.forEach(function (label) {
                            label.pointStyle = 'circle';
                            label.fillStyle = label.strokeStyle;
                        });
                        return labels;
                    }
                },
                onHover: function (e, legendItem, legend) {
                    var _a;
                    if (isXs || isLgDown)
                        return;
                    if ((_a = e.native) === null || _a === void 0 ? void 0 : _a.target) {
                        e.native.target.style.cursor = 'pointer';
                    }
                    var tooltip = document.getElementById('chart-legend-tooltip');
                    if (!tooltip) {
                        tooltip = document.createElement('div');
                        tooltip.id = 'chart-legend-tooltip';
                        tooltip.style.position = 'absolute';
                        tooltip.style.backgroundColor = 'rgba(0,0,0,0.7)';
                        tooltip.style.color = 'white';
                        tooltip.style.padding = '5px 10px';
                        tooltip.style.borderRadius = '4px';
                        tooltip.style.fontSize = '12px';
                        tooltip.style.pointerEvents = 'none';
                        tooltip.style.zIndex = '1000';
                        document.body.appendChild(tooltip);
                    }
                    tooltip.textContent = translate('assets.legendTooltip');
                    if (e.native) {
                        tooltip.style.left = e.native.pageX + 'px';
                        tooltip.style.top = e.native.pageY + 10 + 'px';
                    }
                    tooltip.style.display = 'block';
                },
                onLeave: function (e, legendItem, legend) {
                    var _a;
                    if ((_a = e.native) === null || _a === void 0 ? void 0 : _a.target) {
                        e.native.target.style.cursor = 'default';
                    }
                    var tooltip = document.getElementById('chart-legend-tooltip');
                    if (tooltip) {
                        tooltip.style.display = 'none';
                    }
                }
            },
            tooltip: {
                mode: 'point',
                usePointStyle: true,
                intersect: true,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                padding: 10,
                boxPadding: 5,
                cornerRadius: 4,
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 13
                },
                callbacks: {
                    label: function (context) { return "".concat(context.dataset.label, ": ").concat(context.parsed.y); }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            y: {
                grid: {
                    display: false
                },
                ticks: {
                    display: false
                },
                border: {
                    display: false
                },
                beginAtZero: true
            }
        },
        elements: {
            line: {
                tension: 0.4,
                borderWidth: 2
            },
            point: {
                radius: isXs || isLgDown ? 4 : 2,
                hoverRadius: 6,
                pointStyle: 'circle'
            }
        },
        interaction: {
            mode: 'point',
            intersect: true
        }
    };
    var analyticsGraphData = useGetMachineAnalyticsQuery({
        startDate: analyticsFilters.startDate,
        endDate: analyticsFilters.endDate
    }, { refetchOnMountOrArgChange: true }).data;
    var groupByWeek = function (data) {
        var grouped = {};
        data.forEach(function (item) {
            var date = item.date instanceof Date ? item.date : new Date(item.date);
            var weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            weekStart.setHours(0, 0, 0, 0);
            var weekKey = weekStart.toISOString();
            grouped[weekKey] = (grouped[weekKey] || 0) + item.count;
        });
        return Object.entries(grouped).map(function (_a) {
            var _b = __read(_a, 2), weekStart = _b[0], count = _b[1];
            return ({
                date: new Date(weekStart),
                count: count
            });
        });
    };
    var groupByMonth = function (data) {
        var grouped = {};
        data.forEach(function (item) {
            var date = item.date instanceof Date ? item.date : new Date(item.date);
            var monthKey = "".concat(date.getFullYear(), "-").concat(date.getMonth());
            grouped[monthKey] = (grouped[monthKey] || 0) + item.count;
        });
        return Object.entries(grouped).map(function (_a) {
            var _b = __read(_a, 2), monthKey = _b[0], count = _b[1];
            return ({
                date: new Date(Number.parseInt(monthKey.split('-')[0]), Number.parseInt(monthKey.split('-')[1]), 1),
                count: count
            });
        });
    };
    var daysDifference = dayjs(analyticsFilters.endDate).diff(analyticsFilters.startDate, 'day');
    var formatDateLabel = function (date) {
        var dateObj = date instanceof Date ? date : new Date(date);
        var isWeeklyView = daysDifference > 30 && daysDifference <= 60;
        var isMonthlyView = daysDifference > 60;
        if (isMonthlyView) {
            return dateObj.toLocaleDateString(translate('common.dateLanguageCode1'), {
                year: 'numeric',
                month: 'short'
            });
        }
        if (isWeeklyView) {
            var weekEnd = new Date(dateObj);
            weekEnd.setDate(dateObj.getDate() + 6);
            return "".concat(dateObj.getDate(), " ").concat(dateObj.toLocaleDateString(translate('common.dateLanguageCode1'), { month: 'short' }), " - ").concat(weekEnd.getDate(), " ").concat(weekEnd.toLocaleDateString(translate('common.dateLanguageCode1'), { month: 'short' }));
        }
        return dateObj.toLocaleDateString(translate('common.dateLanguageCode1'), {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    var processActiveChatsData = function () {
        var _a;
        if (!(analyticsGraphData === null || analyticsGraphData === void 0 ? void 0 : analyticsGraphData.activeChatsGraph))
            return { labels: [], datasets: [] };
        var isWeeklyView = daysDifference > 30 && daysDifference <= 60;
        var isMonthlyView = daysDifference > 60;
        var labels = analyticsGraphData.activeChatsGraph.labels.map(function (label) {
            return typeof label === 'string' ? new Date(label) : label;
        });
        if (isWeeklyView || isMonthlyView) {
            var processedDatasets = analyticsGraphData.activeChatsGraph.datasets.map(function (dataset) {
                var dailyData = dataset.data.map(function (count, index) { return ({
                    date: labels[index],
                    count: count
                }); });
                var groupedData = isMonthlyView ? groupByMonth(dailyData) : groupByWeek(dailyData);
                return __assign(__assign({}, dataset), { data: groupedData.map(function (item) { return item.count; }), originalDates: groupedData.map(function (item) { return item.date; }) });
            });
            return {
                labels: ((_a = processedDatasets[0]) === null || _a === void 0 ? void 0 : _a.originalDates) || [],
                datasets: processedDatasets
            };
        }
        return {
            labels: labels,
            datasets: analyticsGraphData.activeChatsGraph.datasets
        };
    };
    var activeChatsData = processActiveChatsData();
    var activeChatsChartData = {
        labels: activeChatsData.labels.map(function (date) { return formatDateLabel(date); }),
        datasets: activeChatsData.datasets.map(function (d, index) { return (__assign(__assign({}, d), { borderColor: graphSetup[index % graphSetup.length].borderColor, backgroundColor: graphSetup[index % graphSetup.length].backgroundColor, pointBackgroundColor: graphSetup[index % graphSetup.length].borderColor, fill: graphSetup[index % graphSetup.length].fill, tension: graphSetup[index % graphSetup.length].tension })); })
    };
    var processAddedAssetsData = function () {
        if (!(analyticsGraphData === null || analyticsGraphData === void 0 ? void 0 : analyticsGraphData.addedMachinesGraph))
            return { labels: [], datasets: [] };
        var isWeeklyView = daysDifference > 30 && daysDifference <= 60;
        var isMonthlyView = daysDifference > 60;
        var rawData = analyticsGraphData.addedMachinesGraph.map(function (item) { return ({
            date: item.date instanceof Date ? item.date : new Date(item.date),
            count: item.count
        }); });
        if (isWeeklyView || isMonthlyView) {
            var groupedData = isMonthlyView ? groupByMonth(rawData) : groupByWeek(rawData);
            return {
                labels: groupedData.map(function (item) { return item.date; }),
                datasets: [
                    {
                        label: translate('assets.assetsOverTime'),
                        data: groupedData.map(function (item) { return item.count; }),
                        borderColor: 'rgba(0, 153, 131, 1)',
                        backgroundColor: 'rgba(0, 153, 131, 0.1)',
                        fill: true,
                        tension: 0.4
                    }
                ]
            };
        }
        return {
            labels: rawData.map(function (item) { return item.date; }),
            datasets: [
                {
                    label: translate('assets.assetsOverTime'),
                    data: rawData.map(function (item) { return item.count; }),
                    borderColor: 'rgba(0, 153, 131, 1)',
                    backgroundColor: 'rgba(0, 153, 131, 0.1)',
                    fill: true,
                    tension: 0.4
                }
            ]
        };
    };
    var groupedAddedAssetsData = processAddedAssetsData();
    var addedAssetsChartData = {
        labels: groupedAddedAssetsData.labels.map(function (date) { return formatDateLabel(date); }),
        datasets: groupedAddedAssetsData.datasets.map(function (dataset) { return (__assign(__assign({}, dataset), { pointBackgroundColor: 'rgba(0, 153, 131, 1)' })); })
    };
    var onWeekChange = function (event) {
        setDaysAnchorEl(event.currentTarget);
    };
    var onWeekClose = function (selectedValue, selectedLabel) {
        setAnalyticsFilters(function (current) { return (__assign(__assign({}, current), { startDate: dayjs().subtract(selectedValue, 'day').toDate(), endDate: dayjs().toDate(), days: selectedValue, dateRange: selectedLabel })); });
        setDaysAnchorEl(null);
    };
    var weekOptions = [
        { value: 7, label: 'audit.days' },
        { value: 10, label: 'audit.days' },
        { value: 14, label: 'audit.days' },
        { value: 30, label: 'audit.days' },
        { value: 45, label: 'audit.days' },
        { value: 60, label: 'audit.days' },
        { value: 90, label: 'audit.days' },
        { value: 183, label: 'audit.lastSixMonths' },
        { value: 365, label: 'audit.lastYear' },
        { value: 0, label: 'audit.customDate' }
    ];
    useEffect(function () {
        var handleScroll = function () {
            setOpenTooltip(false);
            setOpenAddedAssetsTooltip(false);
        };
        if (openTooltip || openAddedAssetsTooltip) {
            window.addEventListener('scroll', handleScroll, true);
        }
        return function () {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openTooltip, openAddedAssetsTooltip]);
    return (_jsxs(Box, { sx: {
            mt: isXs ? 1 : undefined,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
        }, children: [_jsx(Grid, { container: true, spacing: '20px', children: _jsx(Grid, { size: { xs: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: isXs ? 'column' : 'row', gap: 1, justifyContent: 'space-between', alignItems: isXs ? 'undefined' : 'center', children: [_jsx(Typography, { variant: 'h6', children: translate('assets.top5Assets') }), _jsxs(Box, { children: [_jsx(Button, { variant: 'soft', color: 'primary', sx: { whiteSpace: 'nowrap' }, size: isXs || isscreen600900 ? 'small' : 'medium', onClick: onWeekChange, endIcon: _jsx(Calendar04Icon, { size: 18 }), children: translate(analyticsFilters.dateRange, { count: analyticsFilters.days }) }), _jsx(Menu, { anchorEl: daysAnchorEl, open: Boolean(daysAnchorEl), onClose: function () { return setDaysAnchorEl(null); }, slotProps: {
                                            paper: {
                                                sx: {
                                                    maxHeight: '300px !important',
                                                    overflowY: 'auto'
                                                }
                                            },
                                            list: {
                                                sx: {
                                                    minWidth: daysAnchorEl ? daysAnchorEl.clientWidth : 180
                                                }
                                            }
                                        }, children: weekOptions
                                            .filter(function (_a) {
                                            var value = _a.value;
                                            return value !== analyticsFilters.days && value !== 0;
                                        })
                                            .map(function (_a) {
                                            var value = _a.value, label = _a.label;
                                            return (_jsx(MenuItem, { onClick: function () { return onWeekClose(value, label); }, children: translate(label, { count: value }) }, value));
                                        }) })] })] }) }) }), _jsx(Box, { sx: {
                    flex: 1,
                    overflowY: 'auto',
                    pt: 0,
                    maxHeight: isXs
                        ? 'calc(100svh - 355px)'
                        : isLgDown
                            ? 'calc(100svh - 324px)'
                            : 'calc(100svh - 260px)',
                    px: 0.5,
                    pb: isXs || isLgDown ? '8px' : '4px'
                }, children: _jsxs(Grid, { container: true, spacing: '20px', children: [_jsx(Grid, { size: { lg: 8 }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', bgcolor: '#FFFFFF', borderRadius: '24px', boxShadow: theme.customShadows.z24, p: isXs ? '16px 2px' : '16px 24px', children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(BubbleChatIcon, { width: 24, height: 24, color: theme.palette.primary.main }), _jsxs(Box, { display: 'flex', gap: 0.1, alignItems: 'center', children: [_jsx(Typography, { variant: 'subtitle2', children: translate('audit.activeChats') }), (isXs || isLgDown) && (_jsx(ClickAwayListener, { onClickAway: function () { return setOpenTooltip(false); }, children: _jsx("div", { children: _jsx(Tooltip, { arrow: true, open: openTooltip, title: translate('assets.legendTooltip'), placement: 'top', children: _jsx(IconButton, { size: 'Xs', onClick: function (e) {
                                                                                    e.stopPropagation();
                                                                                    setOpenTooltip(function (prev) { return !prev; });
                                                                                }, children: _jsx(InformationCircleIcon, {}) }) }) }) }))] })] }), _jsx(Box, { width: isXs ? '90%' : undefined, children: ((_a = activeChatsChartData === null || activeChatsChartData === void 0 ? void 0 : activeChatsChartData.datasets) === null || _a === void 0 ? void 0 : _a.length) > 0 &&
                                                        activeChatsChartData.datasets.some(function (ds) { return ds.data.length > 0; }) ? (_jsx(Line, { options: options, data: activeChatsChartData, height: 300 })) : (_jsx(Box, { height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', color: theme.palette.text.secondary, children: _jsx(Typography, { variant: 'body2', children: translate('common.noRecordFound') }) })) })] }) }), _jsx(Grid, { size: { xs: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', bgcolor: '#FFFFFF', borderRadius: '24px', boxShadow: theme.customShadows.z24, p: '16px 24px', children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(CellsIcon, { width: 24, height: 24, color: theme.palette.primary.main }), _jsxs(Box, { display: 'flex', gap: 0.1, alignItems: 'center', children: [_jsx(Typography, { variant: 'subtitle2', children: translate('assets.addedAssets') }), (isXs || isLgDown) && (_jsx(ClickAwayListener, { onClickAway: function () { return setOpenAddedAssetsTooltip(false); }, children: _jsx("div", { children: _jsx(Tooltip, { arrow: true, open: openAddedAssetsTooltip, title: translate('assets.legendTooltip'), placement: 'top', children: _jsx(IconButton, { size: 'Xs', onClick: function (e) {
                                                                                            e.stopPropagation();
                                                                                            setOpenAddedAssetsTooltip(function (prev) { return !prev; });
                                                                                        }, children: _jsx(InformationCircleIcon, {}) }) }) }) }))] })] }), _jsx(Typography, { variant: 'h6', children: ((_b = analyticsGraphData === null || analyticsGraphData === void 0 ? void 0 : analyticsGraphData.addedMachinesGraph) === null || _b === void 0 ? void 0 : _b.reduce(function (sum, item) { return sum + ((item === null || item === void 0 ? void 0 : item.count) || 0); }, 0)) || 0 })] }), _jsx(Box, { width: isXs ? '90%' : undefined, children: ((_c = addedAssetsChartData === null || addedAssetsChartData === void 0 ? void 0 : addedAssetsChartData.datasets) === null || _c === void 0 ? void 0 : _c.length) > 0 &&
                                                        addedAssetsChartData.datasets.some(function (ds) { return ds.data.length > 0; }) ? (_jsx(Line, { options: options, data: addedAssetsChartData, height: 235 })) : (_jsx(Box, { height: 235, display: 'flex', justifyContent: 'center', alignItems: 'center', color: theme.palette.text.secondary, children: _jsx(Typography, { variant: 'body2', children: translate('common.noRecordFound') }) })) })] }) })] }) }), _jsx(Grid, { size: { lg: 4 }, sx: { width: '100%' }, children: _jsxs(Grid, { container: true, spacing: '20px', direction: 'column', children: [_jsx(Grid, { size: { xs: 12, md: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', bgcolor: '#FFFFFF', borderRadius: '24px', boxShadow: theme.customShadows.z24, p: '16px 24px', height: '216.2px', children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(BubbleChatIcon, { size: 24, color: theme.palette.primary.main }), _jsx(Typography, { variant: 'subtitle2', children: translate('common.chats') })] }), analyticsGraphData &&
                                                    ((_e = (_d = analyticsGraphData.machineDocumentChats) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 0 ? (_jsx(Grid, { container: true, spacing: 1.2, children: analyticsGraphData.machineDocumentChats.map(function (item, index) {
                                                        var matchedDatasetIndex = activeChatsChartData.datasets.findIndex(function (d) { return d.label === item.name; });
                                                        var color = matchedDatasetIndex === -1
                                                            ? theme.palette.text.disabled
                                                            : graphSetup[matchedDatasetIndex % graphSetup.length].borderColor;
                                                        return (_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: {
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between'
                                                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Box, { sx: {
                                                                                    width: 12,
                                                                                    height: 12,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: color
                                                                                } }), _jsx(Typography, { variant: 'body2', title: item.name, sx: {
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    maxWidth: { xl: 242, lg: 222, sm: 300, xs: 200 }
                                                                                }, children: item.name })] }), _jsx(Typography, { variant: 'caption', children: item.count })] }) }, item.name));
                                                    }) })) : (_jsx(Box, { sx: { padding: '50px 0', textAlign: 'center' }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: translate('common.noRecordFound') }) }))] }) }), _jsx(Grid, { size: { xs: 12, md: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', bgcolor: '#FFFFFF', borderRadius: '24px', boxShadow: theme.customShadows.z24, p: '16px 24px', height: '216.2px', children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(Idea01Icon, { size: 24, color: theme.palette.primary.main }), _jsx(Typography, { variant: 'subtitle2', children: translate('assets.expertOpinions') })] }), analyticsGraphData && ((_g = (_f = analyticsGraphData.expertOpinions) === null || _f === void 0 ? void 0 : _f.length) !== null && _g !== void 0 ? _g : 0) > 0 ? (_jsx(Grid, { container: true, spacing: 1.2, children: analyticsGraphData.expertOpinions.map(function (item, index) { return (_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between'
                                                            }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Box, { sx: {
                                                                                width: 12,
                                                                                height: 12,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: theme.palette.primary.light
                                                                            } }), _jsx(Typography, { variant: 'body2', title: item.name, sx: {
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                maxWidth: { xl: 242, lg: 222, sm: 300, xs: 200 }
                                                                            }, children: item.name })] }), _jsx(Typography, { variant: 'caption', children: item.count })] }) }, item.name)); }) })) : (_jsx(Box, { sx: { padding: '50px 0', textAlign: 'center' }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: translate('common.noRecordFound') }) }))] }) }), _jsx(Grid, { size: { xs: 12, md: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', bgcolor: '#FFFFFF', borderRadius: '24px', boxShadow: theme.customShadows.z24, p: '16px 24px', height: '216.2px', width: '100%', children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(MessageQuestionIcon, { size: 24, color: theme.palette.info.light }), _jsx(Typography, { variant: 'subtitle2', children: translate('common.faqs') })] }), analyticsGraphData && ((_j = (_h = analyticsGraphData.faqs) === null || _h === void 0 ? void 0 : _h.length) !== null && _j !== void 0 ? _j : 0) > 0 ? (_jsx(Grid, { container: true, spacing: 1.2, children: analyticsGraphData === null || analyticsGraphData === void 0 ? void 0 : analyticsGraphData.faqs.map(function (item, index) { return (_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between'
                                                            }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Box, { sx: {
                                                                                width: 12,
                                                                                height: 12,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: theme.palette.info.light
                                                                            } }), _jsx(Typography, { variant: 'body2', title: item.name, sx: {
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                maxWidth: { xl: 242, lg: 222, sm: 300, xs: 200 }
                                                                            }, children: item.name })] }), _jsx(Typography, { variant: 'caption', children: item.count })] }) }, item.name)); }) })) : (_jsx(Box, { sx: { padding: '50px 0', textAlign: 'center' }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: translate('common.noRecordFound') }) }))] }) })] }) })] }) })] }));
}
